import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lib';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { UiTextField, UiIcon } from 'ui-components';
import { usePermission, useOnClickOutside, useRouter } from 'hooks';
import Loading from 'components/loading/Loading';
import DisplayError from 'components/error/DisplayError';
import { fetchFilterResources, resetFilterResources } from 'store';
import {
  getCurrentSlice,
  isNativePlatform,
  magicNumbers,
  removeUrlProtocolParts,
} from 'utils';
import {
  IncludeOrExcludeRadios,
  ResourceList,
  SelectedResourceList,
} from './components';
import { mapToFilterOption } from './filterOption';
import { StyledResourcesFilter } from './ResourcesFilter.styles';

const resourcesFilterDefault = {};

export const ResourcesFilter = (props) => {
  const { componentName, parentPlatform, className } = props;

  const resourcesFilter =
    useSelector((state) =>
      parentPlatform
        ? state[state.filterReducer.filtersContext]?.filters?.[
            parentPlatform
          ]?.[componentName]
        : state[state.filterReducer.filtersContext]?.filters?.[componentName]
    ) || resourcesFilterDefault;
  const reducerName = useSelector(
    (state) => state.filterReducer.filtersContext
  );
  const filterResources = useSelector((state) => {
    let filterResourcesPlatform;
    if (parentPlatform) {
      filterResourcesPlatform = parentPlatform;
    } else if (
      resourcesFilter.platform === 'instagramComment' &&
      resourcesFilter.componentName === 'resourcesFilter'
    ) {
      filterResourcesPlatform = 'instagram';
    } else {
      filterResourcesPlatform = resourcesFilter.platform;
    }

    return state.resourceReducer.filterResources[filterResourcesPlatform];
  });
  const {
    selectedValue,
    selectedItems = {},
    hasIncludeExclude,
    platform,
  } = resourcesFilter || {};
  const { setSelectedItems, setSelectedFilterValue } =
    getCurrentSlice(reducerName);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { clearRouterFilters } = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { userPermission } = usePermission();
  const resourcesFilterRef = useRef(null);
  const currentPlatform = parentPlatform || platform;

  const { permission: hasResourcesSearchPermission } = useMemo(
    () => userPermission('SearchTag', 'index'),
    [userPermission]
  );

  useOnClickOutside(resourcesFilterRef, () => setIsDropdownVisible(false));

  useEffect(() => {
    dispatch(resetFilterResources());
    setSearchQuery('');
  }, [dispatch, platform]);

  const handleSelectedResourceAdd = (item) => {
    dispatch(
      setSelectedItems({
        item,
        isChecked: true,
        componentName,
        parentPlatform,
      })
    );
    setIsDropdownVisible(false);
    setSearchQuery('');
    clearRouterFilters();
  };

  const handleSelectedResourceRemove = (item) => {
    dispatch(
      setSelectedItems({
        item,
        isChecked: false,
        componentName,
        parentPlatform,
      })
    );
    clearRouterFilters();
  };

  const handleSearchTypeChange = (event) => {
    dispatch(
      setSelectedFilterValue({
        value: event.target.value,
        componentName,
        parentPlatform,
      })
    );
  };

  const handleFeatchResources = () => {
    const { params, path } = mapToFilterOption(currentPlatform);
    dispatch(
      fetchFilterResources({
        params: {
          ...params,
          ids: selectedItems
            ? Object.values(selectedItems).map((item) => item.id)
            : undefined,
        },
        path,
      })
    );
  };

  useEffect(() => {
    handleFeatchResources();
  }, [platform]);

  const handleFetchFilterResources = (searchQuery) => {
    if (searchQuery.length >= magicNumbers.MIN_POSSIBLE_LENGTH_OF_WORD) {
      const { params, path } = mapToFilterOption(currentPlatform);
      const isIncludeInNativePlatforms = isNativePlatform({
        platform: currentPlatform,
      });

      dispatch(
        fetchFilterResources({
          searchQuery: removeUrlProtocolParts(searchQuery),
          params,
          path,
          parentPlatform:
            platform === 'instagramComment' &&
            componentName === 'resourcesFilter'
              ? 'instagram'
              : currentPlatform,
          platforms: isIncludeInNativePlatforms
            ? [`${currentPlatform}_channel`]
            : undefined,
        })
      );
    }
  };

  const handleSearchQueryChange = useMemo(() => {
    return debounce(handleFetchFilterResources, magicNumbers.DEBOUNCE_DELAY);
  }, [platform, parentPlatform]);

  const handleSearchQueryEnter = (event) => {
    if (event.key === 'Enter') {
      handleSearchQueryChange(searchQuery);
    }
  };

  if (!hasResourcesSearchPermission) return null;

  let renderedResources;
  if (filterResources?.error) {
    renderedResources = (
      <div className='items-list h-20 flex center'>
        <DisplayError error={filterResources.error} />
      </div>
    );
  } else if (filterResources?.loading) {
    renderedResources = (
      <div className='items-list h-20 flex center'>
        <Loading spinning={true}></Loading>
      </div>
    );
  } else if (filterResources?.data?.length === 0) {
    renderedResources = (
      <div className='items-list h-10 flex center'>
        {t('common:dataNotFound')}
      </div>
    );
  } else if (filterResources?.data?.length > 0) {
    renderedResources = (
      <ResourceList
        filterResources={filterResources.data}
        selectedItems={selectedItems}
        filtersContext={parentPlatform || platform}
        onResourceSelect={handleSelectedResourceAdd}
      />
    );
  }

  return (
    <StyledResourcesFilter>
      <div
        className={classNames('filter-resources', className)}
        ref={resourcesFilterRef}
      >
        {hasIncludeExclude && (
          <IncludeOrExcludeRadios
            onSearchTypeChange={handleSearchTypeChange}
            selectedValue={selectedValue}
            disabled={resourcesFilter?.isDisabled}
            includeLabel={t('filter:onlySelectedResouces')}
            excludeLabel={t('filter:excludeSelectedResources')}
          />
        )}
        <UiTextField
          className='resource-search-input mt-10'
          value={searchQuery}
          onChange={(event) => {
            const { value } = event.target;
            setSearchQuery(value);
            handleSearchQueryChange(value);
          }}
          onClick={() =>
            !resourcesFilter?.isDisabled && setIsDropdownVisible(true)
          }
          onKeyUp={handleSearchQueryEnter}
          placeholder={t('filter:searchPlaceholder')}
          disabled={resourcesFilter?.isDisabled}
          InputProps={{
            startAdornment: (
              <UiIcon
                icon='ri-search-line'
                className='search-icon'
                disabled={filterResources?.loading}
                onClick={() => handleSearchQueryChange(searchQuery)}
              />
            ),
          }}
        />
        {isDropdownVisible && <>{renderedResources}</>}
        {Object.keys(selectedItems).length > 0 && (
          <SelectedResourceList
            selectedItems={selectedItems}
            onSelectedResourceRemove={handleSelectedResourceRemove}
          />
        )}
      </div>
    </StyledResourcesFilter>
  );
};

ResourcesFilter.propTypes = {
  componentName: PropTypes.string.isRequired,
  parentPlatform: PropTypes.string,
  className: PropTypes.string,
};
